import React, { useEffect, useState, useCallback } from 'react';
import { Box, Flex, Text, Card, SimpleGrid, Spinner } from '@chakra-ui/react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import BackButton from 'components/backButton/BackButton';
import CommonInput from 'components/commonInput/CommonInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import CommonSwitchInput from 'components/commonInput/CommonSwitchInput';
import CommonSubmitBtn from 'components/commonInput/CommonSubmitBtn';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import {
  GetLoginUserRegionData,
  EditRegionTaxData,
  GetInvoiceLanguageOptions,
} from 'reactRedux/thunks/regionEditThunk/RegionEditThunk';
import { editRegionLoginUserValidationSchema } from 'utils/CommonValidation';
import {
  currencyOptions,
  sideBarRoutes,
  timezoneOptions,
} from 'utils/StaticData';
import { languageLabel } from 'utils/InvoiceLanguage';
import { DEDUCTION_FIELDS } from './constants';

const RegionalSettingsEdit = () => {
  const { editRegionId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { textColor, brandStars } = useColorModeVariables();
  const [isReady, setIsReady] = useState(false);
  const { isLoading, getRegionData, isEditLoading, invoiceLanguageOptions } =
    useSelector((state) => state.RegionEditSlice);
  const { adminDetails } = useSelector((state) => state.authSlice);
  const isAdmin = adminDetails?.role === sideBarRoutes?.Admin;

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(editRegionLoginUserValidationSchema(false)),
  });

  const { fields, replace } = useFieldArray({ control, name: 'regions' });

  const getRegions = useCallback(() => {
    dispatch(GetLoginUserRegionData());
  }, [dispatch]);

  useEffect(() => {
    getRegions();
    // selectable Local languages come from the backend.
    dispatch(GetInvoiceLanguageOptions());
  }, [getRegions, dispatch]);

  useEffect(() => {
    if (Array.isArray(getRegionData) && getRegionData.length) {
      const match = getRegionData.find(
        (r) => String(r.regionId) === String(editRegionId),
      );
      if (match) {
        replace([
          {
            ...match,
            timezone: match.timezone
              ? { id: match.timezone, label: match.timezone }
              : null,
            currencyOption: match.currency
              ? (currencyOptions.find((c) => c.code === match.currency) ?? {
                  code: match.currency,
                  symbol: match.currencySymbol ?? match.currency,
                  label: match.currency,
                })
              : currencyOptions[0],
            // local (first) language of this region. Drives which
            // languages its invoices may be generated in.
            languageOption: match.language
              ? { id: match.language, label: languageLabel(match.language) }
              : null,
          },
        ]);
        setIsReady(true);
      }
    }
  }, [getRegionData, editRegionId, replace]);

  const onSubmit = (data) => {
    const region = data?.regions?.[0];
    if (!region) return;
    const combinedAddress = `${region.orgName} - ${region.orgAddress} - ${region.orgCode}`;
    const payload = {
      regions: [
        {
          regionId: region.regionId,
          tax: Number(region.tax),
          lostCharge: Number(region.lostCharge),
          profitShare: Number(region.profitShare),
          settlementDays: Number(region.settlementDays),
          orgName: region.orgName,
          orgAddress: region.orgAddress,
          orgCode: region.orgCode,
          address: combinedAddress,
          landmark: region.landmark,
          eInvoiceFee: Number(region.eInvoiceFee),
          defaultIncludePowerbankCost: Boolean(
            region.defaultIncludePowerbankCost,
          ),
          defaultIncludeCustomerPurchases: Boolean(
            region.defaultIncludeCustomerPurchases,
          ),
          defaultIncludeTransactionCost: Boolean(
            region.defaultIncludeTransactionCost,
          ),
          defaultIncludeEInvoiceFee: Boolean(region.defaultIncludeEInvoiceFee),
          ...(region.timezone?.id && { timezone: region.timezone.id }),
          ...(region.currencyOption?.code && {
            currency: region.currencyOption.code,
          }),
          ...(region.currencyOption?.symbol && {
            currencySymbol: region.currencyOption.symbol,
          }),
          ...(region.languageOption?.id && {
            language: region.languageOption.id,
          }),
        },
      ],
    };
    dispatch(
      EditRegionTaxData(payload, () => {
        navigate(`/admin/regional-settings/details/${region.regionId}`);
      }),
    );
  };

  if (isLoading || !isReady) {
    return (
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <BackButton
          fallbackUrl="/admin/regional-settings"
          label="Back to Regional Settings"
        />
        <Flex justify="center" align="center" minH="50vh">
          <Spinner size="xl" color="primary" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <Flex mb={4}>
        <BackButton
          fallbackUrl="/admin/regional-settings"
          label="Back to Regional Settings"
        />
      </Flex>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {fields.map((item, index) => (
          <Box key={item.id}>
            <Card
              w="100%"
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              borderRadius="20px"
              paddingInline="40px"
              paddingBlock="40px"
              mb={4}
              overflowX={{ sm: 'scroll', lg: 'hidden' }}
            >
              <Text fontSize="xl" fontWeight="bold" mb={6}>
                Edit {item.regionName}
              </Text>
              <SimpleGrid
                columns={{ base: 1, sm: 1, md: 2, lg: 3 }}
                spacing="24px"
              >
                <CommonInput
                  type="text"
                  name={`regions.${index}.regionId`}
                  labelText="Region ID"
                  textColor={textColor}
                  brandStars={brandStars}
                  register={register(`regions.${index}.regionId`)}
                  readOnly={true}
                />
                <CommonInput
                  type="number"
                  name={`regions.${index}.tax`}
                  labelText="Tax"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Tax"
                  register={register(`regions.${index}.tax`)}
                  errors={errors?.regions?.[index]?.tax}
                  isValidate={true}
                  isAddPercentage={true}
                />
                <CommonInput
                  type="number"
                  name={`regions.${index}.lostCharge`}
                  labelText="Lost Charge"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Lost Charge"
                  register={register(`regions.${index}.lostCharge`)}
                  errors={errors?.regions?.[index]?.lostCharge}
                  isValidate={true}
                />
                {isAdmin && (
                  <CommonInput
                    type="number"
                    name={`regions.${index}.profitShare`}
                    labelText="Profit Sharing"
                    textColor={textColor}
                    brandStars={brandStars}
                    placeholder="Profit Sharing"
                    register={register(`regions.${index}.profitShare`)}
                    errors={errors?.regions?.[index]?.profitShare}
                    isValidate={true}
                    isAddPercentage={true}
                  />
                )}
                <CommonInput
                  type="number"
                  name={`regions.${index}.settlementDays`}
                  labelText="Settlement Days"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Settlement Days"
                  register={register(`regions.${index}.settlementDays`)}
                  errors={errors?.regions?.[index]?.settlementDays}
                  isValidate={true}
                />
                <CommonInput
                  type="text"
                  name={`regions.${index}.orgName`}
                  labelText="Organization Name"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Organization Name"
                  register={register(`regions.${index}.orgName`)}
                  errors={errors?.regions?.[index]?.orgName}
                  isValidate={true}
                />
                <CommonInput
                  type="text"
                  name={`regions.${index}.orgAddress`}
                  labelText="Organization Address"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Organization Address"
                  register={register(`regions.${index}.orgAddress`)}
                  errors={errors?.regions?.[index]?.orgAddress}
                  isValidate={true}
                />
                <CommonInput
                  type="text"
                  name={`regions.${index}.orgCode`}
                  labelText="Organization Code"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Organization Code"
                  register={register(`regions.${index}.orgCode`)}
                  errors={errors?.regions?.[index]?.orgCode}
                  isValidate={true}
                />
                <CommonInput
                  type="text"
                  name={`regions.${index}.landmark`}
                  labelText="Invoice Generated"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Invoice Generated"
                  register={register(`regions.${index}.landmark`)}
                  errors={errors?.regions?.[index]?.landmark}
                  isValidate={true}
                />
                <CommonInput
                  type="number"
                  name={`regions.${index}.eInvoiceFee`}
                  labelText="E-Invoice Fee"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="E-Invoice Fee"
                  register={register(`regions.${index}.eInvoiceFee`)}
                  errors={errors?.regions?.[index]?.eInvoiceFee}
                  isValidate={true}
                />
                <Controller
                  name={`regions.${index}.timezone`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Timezone"
                      name={`regions.${index}.timezone`}
                      type="select"
                      options={timezoneOptions}
                      value={value}
                      onChange={onChange}
                      register={register}
                      isRequired={true}
                      error={errors?.regions?.[index]?.timezone}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                    />
                  )}
                />
                <Controller
                  name={`regions.${index}.languageOption`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Local Language"
                      name={`regions.${index}.languageOption`}
                      type="select"
                      options={invoiceLanguageOptions}
                      value={value}
                      onChange={onChange}
                      register={register}
                      isRequired={false}
                      error={errors?.regions?.[index]?.languageOption}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                      labelExtra={
                        <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                          invoices use English or this language only
                        </Text>
                      }
                    />
                  )}
                />
                <Controller
                  name={`regions.${index}.currencyOption`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Currency"
                      name={`regions.${index}.currencyOption`}
                      type="select"
                      options={currencyOptions}
                      value={value}
                      onChange={onChange}
                      register={register}
                      isRequired={false}
                      error={errors?.regions?.[index]?.currencyOption}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.code}
                    />
                  )}
                />
              </SimpleGrid>
            </Card>

            <Card
              w="100%"
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              borderRadius="20px"
              paddingInline="40px"
              paddingBlock="40px"
              mb={4}
              overflowX={{ sm: 'scroll', lg: 'hidden' }}
            >
              <Text fontSize="xl" fontWeight="bold" mb={2}>
                Default Merchant Deductions
              </Text>
              <Text fontSize="sm" color="gray.500" mb={4}>
                Applied to new merchants created in this region. Existing
                merchants are not affected.
              </Text>
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing="16px"
                bg="gray.50"
                p={6}
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.200"
              >
                {DEDUCTION_FIELDS.map((deduction) => (
                  <CommonSwitchInput
                    key={deduction.field}
                    labelText={deduction.label}
                    name={`regions.${index}.${deduction.field}`}
                    register={register(`regions.${index}.${deduction.field}`)}
                    isValidate={false}
                    textColor="gray.700"
                    brandStars="red.500"
                    control={control}
                    infoText={deduction.infoText}
                  />
                ))}
              </SimpleGrid>
            </Card>
          </Box>
        ))}

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
          <CommonSubmitBtn BTNtext="Save Changes" isLoading={isEditLoading} />
        </SimpleGrid>
      </form>
    </Box>
  );
};

export default RegionalSettingsEdit;
